import { ReactElement, useMemo, useState, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { get, some } from 'lodash-es'
import numeral from 'numeral'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { Button, Grid, Stack } from '@mui/material'
import { TextField, Select, FormControl, Switch } from '@ui/forms'
import AssemblyPlot from '../../AssemblyPlot'
import { assemblyTypes } from '../../constants'
import VibrationCheckSettingsDialog from '../VibrationCheckSettingsDialog'

const vibrationCheckSettingsFields = ['vibration_check_settings']

const formatAsCM = (value: number) => {
  return numeral(value * 100).format('0.[0]')
}

const formatAsMM = (value: number) => {
  return numeral(value * 1000).format('0.[0]')
}

const fixMaterialIdentifierSW24ToC24 = (materialIdentifier: string) => {
  if (materialIdentifier === 'SW24') {
    return 'C24'
  }
  return materialIdentifier
}

const diaphragmMaterialShortName = (layer: PlateLayer) => {
  const materialThickness = layer.plate.material.thickness
  const materialThicknessFormatted = formatAsMM(materialThickness)
  const uniqueNameLower = layer.plate.material.identifier.toLowerCase()
  if (uniqueNameLower.includes('riridur')) {
    return `GF${materialThicknessFormatted}`
  } else if (uniqueNameLower.includes('rigidur')) {
    return `GF${materialThicknessFormatted}`
  } else if (uniqueNameLower.includes('fegfp')) {
    return `GF${materialThicknessFormatted}`
  } else if (uniqueNameLower.includes('diamantx')) {
    return `GKF${materialThicknessFormatted}`
  } else if (uniqueNameLower.includes('kndi')) {
    return `GKF${materialThicknessFormatted}`
  } else if (uniqueNameLower.includes('osb')) {
    return `OSB${materialThicknessFormatted}`
  } else if (uniqueNameLower.includes('spp4') || uniqueNameLower.includes('spp5')) {
    return `SP${materialThicknessFormatted}`
  } else if (uniqueNameLower.includes('dreischichtplatte')) {
    return `3S${materialThicknessFormatted}`
  }
  return `${layer.plate.material.identifier}${materialThicknessFormatted}`
}

const isTimberFrameWallLayer = (layer: Layer): layer is TimberFrameLayerWall =>
  layer.kind === 'timber-frame-layer' || layer.kind === 'timber-frame-layer-wall'

const isTimberFrameRoofLayer = (layer: Layer): layer is TimberFrameLayerRoofCeiling =>
  layer.kind === 'timber-frame-layer-roof-ceiling'

const determinePrefix = (layers: Layer[], assemblyType: AssemblyTypes) => {
  const timberFrameLayerIndex = layers.findIndex(
    (layer: Layer) => isTimberFrameWallLayer(layer) || isTimberFrameRoofLayer(layer),
  )
  const timberFrameLayer =
    timberFrameLayerIndex !== -1 ? (layers[timberFrameLayerIndex] as TimberFrameLayer) : null
  const cltLayerIndex = layers.findIndex((layer: Layer) => layer.kind === 'clt-assembly-layer')
  const cltLayer = cltLayerIndex !== -1 ? (layers[cltLayerIndex] as CLTAssemblyLayer) : null
  const isTimberFrameAssembly = timberFrameLayer !== null
  const isCLTAssembly = cltLayer !== null
  if (isTimberFrameAssembly) {
    if (assemblyType === 'Inner') {
      return 'IW'
    } else if (assemblyType === 'Outer') {
      return 'AW'
    } else if (assemblyType === 'Roof') {
      return 'Dach'
    } else if (assemblyType === 'Ceiling') {
      return 'Balkendecke'
    }
  } else if (isCLTAssembly) {
    if (assemblyType === 'Inner') {
      return 'IW'
    } else if (assemblyType === 'Outer') {
      return 'AW'
    } else if (assemblyType === 'Roof') {
      return 'Dach'
    } else if (assemblyType === 'Ceiling') {
      return 'Decke'
    }
  } else {
    if (assemblyType === 'Inner') {
      return 'IW'
    } else if (assemblyType === 'Outer') {
      return 'AW'
    } else if (assemblyType === 'Roof') {
      return 'Dach'
    } else if (assemblyType === 'Ceiling') {
      return 'Decke'
    }
  }
  return ''
}

const generateAssemblyName = (layers: Layer[], assemblyType: AssemblyTypes) => {
  // Get assembly type prefix
  const prefix = determinePrefix(layers, assemblyType)
  // Find timber frame layer if it exists
  const timberFrameLayerIndex = layers.findIndex(
    (layer: Layer) => isTimberFrameWallLayer(layer) || isTimberFrameRoofLayer(layer),
  )
  const timberFrameLayer =
    timberFrameLayerIndex !== -1 ? (layers[timberFrameLayerIndex] as TimberFrameLayer) : null
  const cltLayerIndex = layers.findIndex((layer: Layer) => layer.kind === 'clt-assembly-layer')
  const cltLayer = cltLayerIndex !== -1 ? (layers[cltLayerIndex] as CLTAssemblyLayer) : null

  const nameParts = []

  // Add timber frame info if present
  if (timberFrameLayer !== null) {
    if (isTimberFrameRoofLayer(timberFrameLayer)) {
      const { standard_beam_cs } = timberFrameLayer
      const beamWidthCM = formatAsCM(standard_beam_cs.shape.width)
      const beamHeightCM = formatAsCM(standard_beam_cs.shape.height)
      const stepWidthCM = formatAsCM(timberFrameLayer.rip_step_size)
      const material = fixMaterialIdentifierSW24ToC24(standard_beam_cs.material.identifier)
      nameParts.push(`${beamWidthCM}/${beamHeightCM}cm ${material}`)
      nameParts.push(`a=${stepWidthCM}cm`)
    } else if (isTimberFrameWallLayer(timberFrameLayer)) {
      const { standard_rip_cs } = timberFrameLayer
      const ripWidthCM = formatAsCM(standard_rip_cs.shape.width)
      const ripHeightCM = formatAsCM(standard_rip_cs.shape.height)
      const stepWidthCM = formatAsCM(timberFrameLayer.rip_step_size)
      const material = fixMaterialIdentifierSW24ToC24(standard_rip_cs.material.identifier)
      nameParts.push(`${ripWidthCM}/${ripHeightCM}cm ${material}`)
      nameParts.push(`e=${stepWidthCM}cm`)
      const layersBefore = layers.slice(0, timberFrameLayerIndex)
      const layersAfter = layers.slice(timberFrameLayerIndex + 1)
      const plateLayerBefore = layersBefore.findLast(
        (layer: Layer) => layer.kind === 'plate-layer',
      ) as PlateLayer | undefined
      const plateLayerAfter = layersAfter.find((layer: Layer) => layer.kind === 'plate-layer') as
        | PlateLayer
        | undefined
      if (plateLayerBefore) {
        const fastenerDistance = formatAsMM(plateLayerBefore.fastener_distance)
        const plateMaterialBefore = diaphragmMaterialShortName(plateLayerBefore)
        const namePartsBefore = []
        namePartsBefore.push(`SI:`)
        namePartsBefore.push(plateMaterialBefore)
        namePartsBefore.push(`a=${fastenerDistance}mm`)
        nameParts.push(namePartsBefore.join(' '))
      }
      if (plateLayerAfter) {
        const fastenerDistance = formatAsMM(plateLayerAfter.fastener_distance)
        const plateMaterialAfter = diaphragmMaterialShortName(plateLayerAfter)
        const namePartsAfter = []
        namePartsAfter.push(`SA:`)
        namePartsAfter.push(plateMaterialAfter)
        namePartsAfter.push(`a=${fastenerDistance}mm`)

        nameParts.push(namePartsAfter.join(' '))
      }
    }
  } else if (cltLayer !== null) {
    const cltThickness = cltLayer.clt.layers.reduce((acc, layer) => acc + layer.thickness, 0)
    const cltThicknessFormatted = formatAsCM(cltThickness)
    nameParts.push(`BSP${cltThicknessFormatted} cm`)
  }

  // Join all parts with spaces
  return `${prefix}: ${nameParts.join(', ')}`
}

interface Props {
  earthMassForce: number
  allowedUsageTypes?: AssemblyTypes[]
}

const GeneralFields = ({ earthMassForce, allowedUsageTypes }: Props): ReactElement => {
  const {
    watch,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext()
  const layers = watch('layers')
  const assemblyType = watch('usage_type')
  const kind = watch('kind')
  const showVibrationSettings = assemblyType === 'Ceiling'
  // Mirrors `sanitizeAssemblyPayload`: any timber frame kind becomes a wall
  // assembly on save when the usage type is a wall type.
  const showRipBucklingSetting =
    (assemblyType === 'Inner' || assemblyType === 'Outer') &&
    typeof kind === 'string' &&
    kind.startsWith('timber-frame-assembly')
  const typeOptions = useMemo(() => {
    const allOptions = assemblyTypes.map(({ label, usage_type: value }) => ({
      label,
      value,
    }))

    // If allowedUsageTypes is specified, filter to only show allowed types
    if (allowedUsageTypes) {
      return allOptions.filter(option => allowedUsageTypes.includes(option.value))
    }

    return allOptions
  }, [allowedUsageTypes])

  const hasErrors = useMemo(() => {
    if (!submitCount) return false

    return some(vibrationCheckSettingsFields, fieldName => !!get(errors, fieldName))
  }, [errors, submitCount])

  const [openVibrationCheckSettingsDialog, setOpenVibrationCheckSettingsDialog] =
    useState<boolean>(false)

  // Adjust state during rendering when showVibrationSettings changes
  const prevShowVibrationSettings = useRef(showVibrationSettings)
  if (showVibrationSettings !== prevShowVibrationSettings.current) {
    prevShowVibrationSettings.current = showVibrationSettings
    if (!showVibrationSettings) {
      setValue('vibration_check_settings', null)
    }
  }

  const handleClickAutoGenerateName = () => {
    setValue('name', generateAssemblyName(layers, assemblyType))
  }
  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl label="Illustration">
            <AssemblyPlot
              assemblyType={assemblyType}
              layers={layers}
              earthMassForce={earthMassForce}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="column" spacing={1} alignItems="center">
            <TextField
              label="Name"
              name="name"
              placeholder="120ger Wand"
              data-cy="assembly-name"
              fullWidth
              // disable password manager recognition
              autoComplete="off"
              inputProps={{
                autoComplete: 'off',
                'data-1p-ignore': true,
                'data-form-type': 'other',
              }}
              multiline
              rows={3}
              autoresize
            />
            <Stack direction="row" spacing={1} alignItems={'center'}>
              <Button
                variant="outlined"
                onClick={handleClickAutoGenerateName}
                startIcon={<AutoFixHighIcon />}
                sx={{ minWidth: 'auto', px: 1 }}
                title="Namen automatisch generieren"
              >
                Auto
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Kurzbez."
            name="short_name"
            placeholder="W1"
            data-cy="assembly-short-name"
          />
        </Grid>
        <Grid item xs={12}>
          <Select
            name="usage_type"
            label="Typ"
            options={typeOptions}
            data-cy="assembly-usage-type"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField label="Beschreibung" name="description" rows={3} multiline />
        </Grid>
        {showRipBucklingSetting && (
          <Grid item xs={12}>
            <FormControl
              label="Nachweiseinstellungen"
              tooltip={{
                text: 'Ist die Rippe durch die Beplankung in Wandebene gehalten, wird nur das Knicken aus der Wandebene nachgewiesen (k_c,z = 1,0) und das Kippen gilt als verhindert (k_crit = 1,0). Ohne Halterung werden das Knicken in Wandebene und das Kippen über die volle Rippenlänge nachgewiesen.',
              }}
            >
              <Switch
                name="rips_held_by_plate"
                label="Rippen durch Beplankung in Wandebene gehalten"
                size="small"
                data-cy="assembly-rips-held-by-plate"
              />
            </FormControl>
          </Grid>
        )}
        {showVibrationSettings && (
          <Grid item xs={12}>
            <FormControl label="Nachweiseinstellungen">
              <Button
                onClick={() => setOpenVibrationCheckSettingsDialog(true)}
                sx={{
                  color: ({ palette }) => (hasErrors ? `${palette.error.main}` : ''),
                }}
              >
                Schwingungsnachweise
              </Button>
            </FormControl>
          </Grid>
        )}
      </Grid>
      {showVibrationSettings && openVibrationCheckSettingsDialog && (
        <VibrationCheckSettingsDialog
          open
          onClose={() => setOpenVibrationCheckSettingsDialog(false)}
          onReset={() => {
            setOpenVibrationCheckSettingsDialog(false)
            setValue('vibration_check_settings', null)
          }}
        />
      )}
    </>
  )
}

export default GeneralFields
